<script>
import $ from 'jquery';
import 'select2/dist/js/select2.full';
import 'select2/dist/js/i18n/de';
import cloneDeepWith from "lodash.clonedeepwith";
import isEqual from "lodash.isequal";

let theme = 'bootstrap-5';

export function setTheme(themeToSet) {
    theme = themeToSet;
}

export default {
    name: 'select2',
    data() {
        return {
            select2: null
        };
    },
    emits: ['update:modelValue', 'select'],
    props: {
        modelValue: [String, Array, Number, null],
        id: {
            type: String,
            default: ''
        },
        name: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        options: {
            type: [Array, Promise],
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        required: {
            type: Boolean,
            default: false
        },
        settings: {
            type: Object,
            default: () => {return {}}
        },
        createTag: {
            type: Function,
            default: undefined
        },
        class: {
            type: [Array, String],
            default: 'form-select',
        },
    },
    watch: {
        options: {
            handler(newOptions, oldOptions) {
                if (!isEqual(newOptions, oldOptions)) {
                    this.setOptions(newOptions);
                }
            },
            deep: true
        },
        modelValue: {
            handler(value) {
                this.setValue(value);
            },
            deep: true
        },
        disabled: {
            handler(value) {
                if (value) {
                    this.destroySelect();
                } else {
                    this.initialize();
                }
            }
        }
    },
    methods: {
        getSettings() {
            let mySettings = cloneDeepWith(this.settings);
            if (mySettings.theme === undefined) {
                mySettings.theme = theme;
            }
            if (mySettings.width === undefined) {
                mySettings.width = '100 %';
            }
            if (mySettings.dropdownParent === undefined) {
                mySettings.dropdownParent = this.$el?.parentElement.closest('.modal') ?? this.$el?.parentElement;
            }
            return mySettings;
        },
        setOptions(value = []) {
            if (this.select2 === null) {
                return;
            }
            const resetOptions = (options) => {
                this.select2.empty();
                this.select2.select2({
                    placeholder: this.placeholder,
                    ...this.getSettings(),
                    data: options,
                    language: 'de',
                    createTag: this.createTag,
                });
                this.setValue(this.modelValue);
            };
            if (value instanceof Promise) {
                value.then(options => resetOptions(options));
            } else {
                resetOptions(value);
            }
        },
        setValue(value) {
            if (this.select2 === null) {
                return;
            }
            if (value instanceof Array) {
                this.select2.val([...value]);
            } else {
                this.select2.val([value]);
            }
            this.select2.trigger('change');
        },
        isArrayControl() {
            return (this.settings.multiple === true);
        },
        emptyVal() {
            if (this.isArrayControl()) {
                return [];
            } else {
                return null;
            }
        },
        initialize() {
            const initSelect2 = (options) => {
                if (!this.$refs.select) {
                    return;
                }
                this.select2 = $(this.$refs.select)
                    .select2({
                        placeholder: this.placeholder,
                        ...this.getSettings(),
                        data: options,
                        language: 'de',
                        createTag: this.createTag,
                    })
                    .on('select2:select select2:unselect', ev => {
                        this.$emit('update:modelValue', this.select2.val() ?? this.emptyVal());
                        this.$emit('select', ev['params']['data']);
                        this.$refs.select.dispatchEvent(new Event('change', {bubbles: true}));
                    })
                    .on('select2:open', function(ev) {
                        const searchInput = this.parentElement.querySelector('input[type="search"]');
                        if (searchInput !== null) {
                            searchInput.value = Array.from($(this).select2('data')).filter(o => o.id = ev.currentTarget.value)?.[0]?.text;
                            searchInput.select();
                        }
                    });
                this.setValue(this.modelValue);
            };
            if (this.options instanceof Promise) {
                this.options.then(options => {
                    initSelect2(options);
                });
            } else {
                setTimeout(() => {
                    initSelect2(this.options);
                });
            }
        },
        destroySelect() {
            if (this.select2 !== null) {
                try {
                    this.select2.select2('destroy');
                } catch (e) {
                    console.error(e, 'couldn\'t destroy select2', this.select2);
                }
            }
        }
    },
    mounted() {
        if (!this.disabled) {
            this.initialize();
        }
    },
    beforeUnmount() {
        this.destroySelect();
    }
};
</script>

<template>
    <input :class="class" :value="modelValue instanceof Array ? modelValue.join(', ') : modelValue" :disabled="true" :id="id" :name="name" :required="required" v-if="disabled" />
    <select ref="select" :class="class" :id="id" :name="name" :required="required" v-if="!disabled"></select>
</template>
